import { computed, ref } from 'vue'
import { api, type DeadLetterDto, type DeadLetterSearchRequest, type DeadLetterSearchResultDto } from '@/api/eOrdersApi'
import { useApiAdapter } from '@/store'

type LoadingStatus = 'idle' | 'pending' | 'success' | 'error'

export interface DeadLetterFilters {
  envelopeId?: string
  externalReference?: string
  queue?: string
  messageType?: string
  exceptionType?: string
  exceptionMessage?: string
  from?: string
  until?: string
}

export const useDeadLettersData = () => {
  const apiAdapter = useApiAdapter()
  
  const rawData = ref<DeadLetterSearchResultDto | null>(null)
  const status = ref<LoadingStatus>('idle')
  const error = ref<string | null>(null)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const currentFilters = ref<DeadLetterFilters>({})
  
  const data = computed<DeadLetterDto[]>(() => rawData.value?.data ?? [])
  const truncated = computed(() => rawData.value?.truncated ?? false)
  
  const pagination = computed(() => {
    if (!rawData.value) return null
    
    const totalItems = rawData.value.totalItems ?? 0
    
    return {
      pageSize: pageSize.value,
      currentPage: currentPage.value,
      totalItems: totalItems,
      totalPages: Math.ceil(totalItems / pageSize.value) || 1
    }
  })
  
  const buildRequest = (size: number, page: number, filters: DeadLetterFilters): DeadLetterSearchRequest => ({
    pageSize: size,
    pageNumber: page,
    ...filters,
  })
  
  const fetchDeadLetters = async (
    size: number = pageSize.value,
    page: number = currentPage.value,
    filters: DeadLetterFilters = currentFilters.value,
    suppressStatusChange = false
  ) => {
    try {
      if (!suppressStatusChange) {
        status.value = 'pending'
      }
      
      error.value = null
      
      const response = await apiAdapter.invoke(config =>
        api.searchDeadLetters(buildRequest(size, page, filters), config)
      )
      
      if (!response) {
        throw new Error('Failed to load dead letters.')
      }
      
      rawData.value = response
      status.value = 'success'
      currentPage.value = page
      pageSize.value = size
      currentFilters.value = filters
    } catch (err) {
      console.error('Failed to fetch dead letters', err)
      status.value = 'error'
      error.value = err instanceof Error ? err.message : 'Unknown error'
      rawData.value = null
    }
  }
  
  const applyFilters = async (filters: DeadLetterFilters) => {
    currentFilters.value = filters
    currentPage.value = 1
    
    await fetchDeadLetters(pageSize.value, 1, filters, true)
  }
  
  const goToPage = async (page: number) => {
    if (page < 1) return
    
    await fetchDeadLetters(pageSize.value, page, currentFilters.value)
  }

  const changePageSize = async (newSize: number) => {
    if (newSize === pageSize.value) return
    
    await fetchDeadLetters(newSize, 1, currentFilters.value)
  }
  
  const replay = async (ids: string[]) => {
    const result = await apiAdapter.invokeRaw(
      config => api.replay({ ids }, config) as any
    )

    return result.type === 'success'
  }

  const remove = async (ids: string[]) => {
    const result = await apiAdapter.invokeRaw(
      config => api.deadLetters({ ids }, config) as any
    )

    return result.type === 'success'
  }
  
  return {
    data,
    truncated,
    status,
    error,
    pagination,
    currentFilters: computed(() => currentFilters.value),
    fetchDeadLetters,
    applyFilters,
    goToPage,
    changePageSize,
    replay,
    remove,
  }
}
